import ColorPicker from "components/basic/color-picker/ColorPicker";
import { confirmPopup } from "primereact/confirmpopup";
import { DropdownChangeEvent } from "primereact/dropdown";
import { normalizeDataTableParams } from "utils/utils";
import { T } from "services/lang/LanguageService";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column, ColumnBodyOptions, ColumnEditorOptions } from "primereact/column";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import React, { ChangeEvent } from "react";
import { HighlightOptions, HilightsEditorProps } from "../DatasetStatusIndicatorTypes";

const emptyHighlight: HighlightOptions = {
  minValue: 0,
  includeMinValue: false,
  maxValue: 0,
  includeMaxValue: false,
  color: {
    color: "#000000",
    bgColor: undefined,
  },
};

const HilightsEditor = ({ currentConfig, onConfigChange }: HilightsEditorProps) => {
  const updateHighlights = (newHighlights: HighlightOptions[]) => {
    const newConfig = { ...currentConfig, highlights: newHighlights };
    onConfigChange(newConfig);
  };

  const updateHighlightCell = (index: number, field: keyof HighlightOptions, value: any) => {
    const list = [...(currentConfig.highlights ?? [])];
    list[index] = { ...list[index], [field]: value };
    updateHighlights(list);
  };

  const addHighlight = () => updateHighlights([...(currentConfig.highlights ?? []), emptyHighlight]);

  const confirmOnDelete = (index: number) => {
    confirmPopup({
      message: T("confirm-popup.do-you-want-to-delete-this-highlight"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: () => deleteHighlight(index),
    });
  };

  const deleteHighlight = (index: number) => {
    const list = [...(currentConfig.highlights ?? [])];
    list.splice(index, 1);
    updateHighlights(list);
  };

  const deleteTemplate = (options: ColumnBodyOptions) => (
    <Button
      icon="pi pi-times"
      className="p-button-text p-button-danger"
      onClick={() => confirmOnDelete(options.rowIndex)}
    />
  );

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const { newData, index } = e;
    const list = [...(currentConfig.highlights ?? [])];
    const oldRow = list[index];

    const updatedRow = {
      ...oldRow,
      ...newData,
      color: newData.color ? { ...oldRow.color, ...newData.color } : oldRow.color,
    };

    list[index] = updatedRow;
    updateHighlights(list);
  };

  const numberCell = <T extends keyof HighlightOptions>(
    field: T,
    bodyParams?: ColumnBodyOptions,
    editorParams?: ColumnEditorOptions
  ) => {
    const { mode, rowData, editorCallback, rowIndex } = normalizeDataTableParams(bodyParams, editorParams);

    return (
      <InputNumber
        value={rowData[field]}
        disabled={mode === "view"}
        onChange={e => {
          editorCallback?.(e.value);
          if (mode === "edit") {
            updateHighlightCell(rowIndex, field, e.value);
          }
        }}
      />
    );
  };

  const checkBoxCell = <T extends keyof HighlightOptions>(
    field: T,
    bodyParams?: ColumnBodyOptions,
    editorParams?: ColumnEditorOptions
  ) => {
    const { mode, rowData, editorCallback, rowIndex } = normalizeDataTableParams(bodyParams, editorParams);

    return (
      <Checkbox
        checked={rowData[field]}
        disabled={mode === "view"}
        onChange={e => {
          editorCallback?.(e.target.checked);
          if (mode === "edit") {
            updateHighlightCell(rowIndex, field, e.target.checked);
          }
        }}
      />
    );
  };

  const handleColorValueChange = (
    e: DropdownChangeEvent | ChangeEvent<HTMLInputElement>,
    field: string,
    color: any,
    rowIndex: number,
    editorCallback?: (value: any) => void,
    mode?: string
  ) => {
    const updated = { ...color, [field]: e.target.value };
    editorCallback?.(updated[field]);
    if (mode === "edit") {
      updateHighlightCell(rowIndex, "color", updated);
    }
  };

  const textColorCell = (bodyParams?: ColumnBodyOptions, editorParams?: ColumnEditorOptions) => {
    const { mode, rowData, editorCallback, rowIndex } = normalizeDataTableParams(bodyParams, editorParams);
    const color = rowData.color || {};

    return (
      <ColorPicker
        value={color.color}
        disabled={mode === "view"}
        onValueChange={e => handleColorValueChange(e, "color", color, rowIndex, editorCallback, mode)}
      />
    );
  };

  const bgColorCell = (bodyParams?: ColumnBodyOptions, editorParams?: ColumnEditorOptions) => {
    const { mode, rowData, editorCallback, rowIndex } = normalizeDataTableParams(bodyParams, editorParams);
    const color = rowData.color || {};

    const handleResetBgColor = () => {
      const updated = { ...color, bgColor: undefined };
      editorCallback?.(undefined);
      updateHighlightCell(rowIndex, "color", updated);
    };

    return (
      <div className="flex gap-2 align-items-center">
        <ColorPicker
          value={color.bgColor ?? ""}
          disabled={mode === "view"}
          onValueChange={e => handleColorValueChange(e, "bgColor", color, rowIndex, editorCallback, mode)}
          enableTransparent
        />
        {mode === "edit" && (
          <Button
            icon="pi pi-times"
            className="p-button-text p-button-danger p-button-sm"
            onClick={handleResetBgColor}
          />
        )}
      </div>
    );
  };

  const handleDefaultColorChange = (
    e: React.ChangeEvent<HTMLInputElement> | DropdownChangeEvent,
    field: string
  ) => {
    onConfigChange({ ...currentConfig, [field]: e.target.value });
  };

  return (
    <div className="flex flex-column gap-2">
      <div className="flex flex-row justify-content-between">
        <div className="flex flex-row gap-2">
          <div className="flex flex-column gap-2">
            <label htmlFor="defaultTextColor">{T("label.default-text-color")}</label>
            <ColorPicker
              id="defaultTextColor"
              value={currentConfig.defaultTextColor ?? ""}
              onValueChange={e => handleDefaultColorChange(e, "defaultTextColor")}
            />
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor="defaultBgColor">{T("label.default-bg-color")}</label>
            <ColorPicker
              id="defaultBgColor"
              value={currentConfig.defaultBgColor ?? ""}
              onValueChange={e => handleDefaultColorChange(e, "defaultBgColor")}
              enableTransparent
            />
          </div>
        </div>
        <Button
          label={T("word.add")}
          icon="pi pi-plus"
          className="component-editor-button icon-green w-1 align-self-end"
          onClick={addHighlight}
        />
      </div>
      <DataTable
        value={currentConfig.highlights ?? []}
        editMode="row"
        size="small"
        onRowEditComplete={onRowEditComplete}
      >
        <Column
          field="minValue"
          header={T("word.min")}
          body={options => numberCell("minValue", options)}
          editor={options => numberCell("minValue", undefined, options)}
        />
        <Column
          field="includeMinValue"
          header={T("word.include")}
          style={{ textAlign: "center" }}
          body={options => checkBoxCell("includeMinValue", options)}
          editor={options => checkBoxCell("includeMinValue", undefined, options)}
        />
        <Column
          field="maxValue"
          header={T("word.max")}
          body={options => numberCell("maxValue", options)}
          editor={options => numberCell("maxValue", undefined, options)}
        />
        <Column
          field="includeMaxValue"
          header={T("word.include")}
          style={{ textAlign: "center" }}
          body={options => checkBoxCell("includeMaxValue", options)}
          editor={options => checkBoxCell("includeMaxValue", undefined, options)}
        />
        <Column
          field="color.color"
          header={T("word.text-color")}
          body={options => textColorCell(options)}
          editor={options => textColorCell(undefined, options)}
        />
        <Column
          field="color.bgColor"
          header={T("word.background-color")}
          body={options => bgColorCell(options)}
          editor={options => bgColorCell(undefined, options)}
        />
        <Column rowEditor headerStyle={{ width: "8rem" }} bodyStyle={{ textAlign: "end", width: "9%" }} />
        <Column body={deleteTemplate} style={{ width: "1%" }} />
      </DataTable>
    </div>
  );
};

export default HilightsEditor;
